import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faCalendarDays,
  faChessKnight,
  faListOl,
  faEye,
  faFire,
  faMagnifyingGlass,
  faPlus,
  faLightbulb,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { HomeSample } from '../types/CorrectionModel';
import { MoveType } from '../types/MoveType';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import { speedLook } from './GamesFiltersTimeControls';
import { moveBetween, solutionPiece, uciToSan } from './correctionGrading';
import { awardForAttempt, syncWalletFromServer } from '../utils/earnings';
import CorrectionsAPI from '../api/CorrectionsAPI';
import { analytics } from '../utils/analytics';
import CorrectionBoard, { CorrectionOutcome, LinePosition } from './CorrectionBoard';

// The home page trainer: a mistake on a big board and one thing to do, make a move. Signed out it
// is a strong player's blunder (named, so it reads as a demo); signed in it is one of your own.
// Puzzle is also the board of the trainer on your own games (FixContainer).

// A solve is loud: confetti over the board, a cheer, a running count.
const CHEERS = ['Brilliant find!', 'Nailed it!', 'Great eye!', "That's the one!", 'Superb!'];

const CONFETTI_COLOURS = ['#e9b949', '#22c55e', '#3b82f6', '#e5533c', '#a855f7', '#f0883e'];

// A burst of confetti from the middle of the board. Plain DOM and one CSS animation, removed when
// it ends; skipped for people who asked their system for less motion.
function confetti(anchor: HTMLElement | null): void {
  if (anchor == null || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const layer = document.createElement('div');
  layer.className = 'sc-confetti';
  for (let i = 0; i < 90; i++) {
    const bit = document.createElement('i');
    const angle = Math.random() * Math.PI * 2;
    const distance = 120 + Math.random() * 260;
    bit.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
    bit.style.setProperty('--dy', `${Math.sin(angle) * distance + 140}px`);
    bit.style.setProperty('--r', `${Math.random() * 720 - 360}deg`);
    bit.style.setProperty('--d', `${Math.random() * 120}ms`);
    bit.style.background = CONFETTI_COLOURS[i % CONFETTI_COLOURS.length];
    layer.appendChild(bit);
  }
  anchor.appendChild(layer);
  window.setTimeout(() => layer.remove(), 2200);
}

export function platformLabel(platform: string | null): string {
  return platform === 'Lichess' ? 'Lichess' : 'Chess.com';
}

// "a blunder", "an inaccuracy", "the best move": the verdict on a move, in words.
function verdictWords(moveType: MoveType | null | undefined): string {
  switch (moveType) {
    case MoveType.Blunder:
      return 'a blunder';
    case MoveType.Mistake:
      return 'a mistake';
    case MoveType.Miss:
      return 'a miss';
    case MoveType.Inaccuracy:
      return 'an inaccuracy';
    case MoveType.Brilliant:
      return 'brilliant';
    case MoveType.Great:
      return 'a great move';
    case MoveType.Best:
      return 'the best move';
    case MoveType.Excellent:
      return 'excellent';
    case MoveType.Good:
      return 'a good move';
    default:
      return 'not it';
  }
}

export function MoveTypeIcon({
  moveType,
}: {
  moveType: MoveType | null | undefined;
}): React.ReactNode {
  if (moveType == null) return null;
  const Icon = getIcon(moveType);
  return Icon ? <Icon className="sc-home-verdict-icon" aria-hidden /> : null;
}

export function formatWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

// The game a position came from, as a row of pills: where, how fast, when. The site pill links to
// the game there; "Moves & notes" opens the whole game here.
export function GameInfo(props: {
  platform: string | null;
  timeControl: string | null;
  date: string;
  link: string | null;
  onOpenGame?: () => void;
}): React.ReactNode {
  const { platform, timeControl, date, link, onOpenGame } = props;
  const site = (
    <>
      <FontAwesomeIcon icon={faChessKnight} />
      {platformLabel(platform)}
      {link ? <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="sc-pill-ext" /> : null}
    </>
  );
  return (
    <p className="sc-home-game">
      {link ? (
        <a
          className="sc-pill is-link"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          title={`Open this game on ${platformLabel(platform)}`}
        >
          {site}
        </a>
      ) : (
        <span className="sc-pill">{site}</span>
      )}
      {timeControl ? (
        <span className={`sc-pill sc-pill-tc ${speedLook(timeControl).cls}`}>
          <FontAwesomeIcon icon={speedLook(timeControl).icon} />
          {timeControl}
        </span>
      ) : null}
      <span className="sc-pill">
        <FontAwesomeIcon icon={faCalendarDays} />
        {formatWhen(date)}
      </span>
      {onOpenGame ? (
        <button type="button" className="sc-pill is-link" onClick={onOpenGame}>
          <FontAwesomeIcon icon={faListOl} />
          Moves &amp; notes
        </button>
      ) : null}
    </p>
  );
}

export function Player(props: {
  name: string | null;
  rating: number | null;
  color: 'white' | 'black';
  blundered: boolean;
}): React.ReactNode {
  return (
    <span className={`sc-home-player${props.blundered ? ' is-blunderer' : ''}`}>
      <span className={`sc-home-disc sc-home-disc--${props.color}`} aria-hidden />
      <span className="sc-home-player-name">{props.name ?? '?'}</span>
      {props.rating != null ? <span className="sc-home-player-rating">{props.rating}</span> : null}
    </span>
  );
}

function OwnGamesForm(): React.ReactNode {
  return (
    <form className="sc-home-own" method="get" action="/try">
      <p className="sc-home-own-title">Fix your own games to avoid making these mistakes.</p>
      <p className="sc-home-own-sub">
        Put in your username. We find the blunders in your recent games and you fix them.
      </p>
      <div className="sc-home-own-row">
        <label className="visually-hidden" htmlFor="sc-home-own-handle">
          Your handle
        </label>
        <input
          id="sc-home-own-handle"
          className="form-control"
          type="text"
          name="handle"
          placeholder="Your Chess.com or Lichess username"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
        />
        <label className="visually-hidden" htmlFor="sc-home-own-platform">
          Platform
        </label>
        <select id="sc-home-own-platform" className="form-select" name="platform">
          <option value="chesscom">Chess.com</option>
          <option value="lichess">Lichess</option>
        </select>
        <button
          className="btn btn-primary"
          type="submit"
          data-track="try_start_click"
          data-ui-source="home_trainer"
        >
          Show my blunders
        </button>
      </div>
    </form>
  );
}

// The signed-in page's box under the verdict: the way to the whole library (search lives there), and
// to adding games. With no mistakes of their own yet, adding games is the thing to do.
function LibraryBox(props: { mine: boolean; waiting: number }): React.ReactNode {
  return (
    <div className="sc-home-own">
      {props.mine ? (
        <>
          <p className="sc-home-own-title">Your games</p>
          <p className="sc-home-own-sub">
            {props.waiting > 1
              ? `${props.waiting} more of your mistakes are waiting.`
              : 'Find any game you played: by opening, opponent, result or date.'}
          </p>
        </>
      ) : (
        <>
          <p className="sc-home-own-title">Add your games to fix your own mistakes.</p>
          <p className="sc-home-own-sub">
            Connect Chess.com or Lichess. We find the blunders in your games and you fix them.
          </p>
        </>
      )}
      <div className="sc-home-own-row">
        {props.mine ? (
          <>
            <a className="btn btn-primary" href="/#/games">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" />
              Search my games
            </a>
            <a className="btn btn-outline-secondary" href="/#/?import=1">
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Add games
            </a>
          </>
        ) : (
          <>
            <a className="btn btn-primary" href="/#/?import=1">
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              Add my games
            </a>
            <a className="btn btn-outline-secondary" href="/#/games">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" />
              Search games
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// "a blunder" / "a mistake": the title's word for the move being repaired, in its colour.
export function mistakeWord(moveType: MoveType): { article: string; word: string } {
  switch (moveType) {
    case MoveType.Mistake:
      return { article: 'a', word: 'mistake' };
    case MoveType.Miss:
      return { article: 'a', word: 'miss' };
    case MoveType.Inaccuracy:
      return { article: 'an', word: 'inaccuracy' };
    default:
      return { article: 'a', word: 'blunder' };
  }
}

export function Puzzle(props: {
  sample: HomeSample;
  // The player's own mistake (signed in), or a stranger's blunder (the demo).
  mine: boolean;
  authenticated: boolean;
  solvedCount: number;
  onSolved: () => void;
  onNext: () => void;
  // Under the board and panel, full width: the trainer's Previous / Next.
  nav?: React.ReactNode;
  // Under the verdict.
  footer?: React.ReactNode;
  // Between the players and the verdict, so a verdict appearing doesn't move it.
  list?: React.ReactNode;
  // Opens the whole game (moves, notes) over the trainer.
  onOpenGame?: () => void;
}): React.ReactNode {
  const {
    sample,
    mine,
    authenticated,
    solvedCount,
    onSolved,
    onNext,
    nav,
    footer,
    list,
    onOpenGame,
  } = props;
  const item = sample.Item;
  const [outcome, setOutcome] = useState<CorrectionOutcome | null>(null);
  const [hint, setHint] = useState(false);
  const [revealed, setRevealed] = useState(false);
  // Solved once already: a retry after that is practice, so it neither pays nor costs.
  const [solved, setSolved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const boardRef = useRef<HTMLDivElement>(null);
  // The board's step controls, from the keyboard too: left and right step a move, up and down jump
  // to the start and to the position to solve, the way chess sites do it.
  const navRef = useRef<((index: number) => void) | null>(null);
  const cursorRef = useRef(item.History.length);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (e.altKey || e.ctrlKey || e.metaKey || target?.closest('input, select, textarea')) return;
      const go = navRef.current;
      if (go == null) return;
      const to =
        e.key === 'ArrowLeft'
          ? cursorRef.current - 1
          : e.key === 'ArrowRight'
            ? cursorRef.current + 1
            : e.key === 'ArrowUp'
              ? 0
              : e.key === 'ArrowDown'
                ? Number.MAX_SAFE_INTEGER
                : null;
      if (to == null) return;
      e.preventDefault();
      go(to);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const history = useMemo<LinePosition[]>(() => {
    const fens = item.History.length > 0 ? item.History : [item.Fen];
    const line = fens[fens.length - 1] === item.Fen ? fens : [...fens, item.Fen];
    return line.map((fen, i) => ({
      fen,
      lastMove: i === 0 ? undefined : (moveBetween(line[i - 1], fen) ?? undefined),
    }));
  }, [item]);

  // The move that led to this position, as it is written in a score sheet: "29... Rd8".
  const lastMove = useMemo(() => {
    if (history.length < 2) return null;
    const prev = history[history.length - 2];
    const squares = history[history.length - 1].lastMove;
    if (squares == null) return null;
    const san = uciToSan(prev.fen, squares[0] + squares[1]);
    if (san == null) return null;
    const blackMoved = item.SideToMove === 'white';
    const number = blackMoved ? item.Number - 1 : item.Number;
    return `${number}${blackMoved ? '...' : '.'} ${san}`;
  }, [history, item]);

  const piece = useMemo(() => solutionPiece(item.Fen, item.Solution), [item]);
  const answerSan = uciToSan(item.Fen, item.Solution) ?? item.Solution;
  const blunderer = item.SideToMove;
  const sideWord = item.SideToMove === 'white' ? 'White' : 'Black';

  // Signed in, every answer is a real repair attempt: it moves the spaced-repetition schedule and
  // the wallet on the server, exactly as the repair session does. A guest's money lives in the tab.
  const record = (success: boolean, dropPct: number | null) => {
    if (!authenticated) return;
    CorrectionsAPI.recordAttempt(item.MoveId, item.GameId, success, hint, dropPct)
      .then(syncWalletFromServer)
      .catch(() => undefined);
  };

  const onResult = (result: CorrectionOutcome) => {
    setOutcome(result);
    analytics.track('home_fix_answer', { result: result.correct ? 'found' : 'missed' });
    if (result.correct) {
      // Shown the answer: no pay, it was given (and the reveal already counted as a miss). A hint
      // halves it, same as everywhere else.
      if (solved) {
        confetti(boardRef.current);
      } else if (!revealed) {
        awardForAttempt({
          success: true,
          swingPoints: item.PlayedSwing,
          priorSuccesses: item.PayoutPriorSuccesses ?? 0,
          hintUsed: hint,
          anchor: boardRef.current,
        });
        record(true, result.dropPct);
        confetti(boardRef.current);
        onSolved();
        setSolved(true);
      }
    } else if (mine && !solved) {
      // A wrong answer on your own mistake costs what that move gives up, as in the repair session.
      awardForAttempt({
        success: false,
        swingPoints: item.PlayedSwing,
        priorSuccesses: item.PayoutPriorSuccesses ?? 0,
        hintUsed: hint,
        lossPoints: result.dropPct,
        anchor: boardRef.current,
      });
      record(false, result.dropPct);
    }
  };

  const retry = () => {
    setOutcome(null);
    setAttempt((a) => a + 1);
  };

  const showAnswer = () => {
    // Being shown counts as not knowing it, so spaced repetition brings it back soon. No charge.
    if (!revealed && mine && !solved) record(false, 0);
    setRevealed(true);
    setHint(true);
    if (outcome != null) retry();
    analytics.track('home_fix_answer', { result: 'shown' });
  };

  const verdictClass = moveTypeClass(outcome?.moveType ?? null) || 'good';

  return (
    <div className="sc-home-grid">
      {/* The game's details above the board. */}
      <div className="sc-home-info">
        <GameInfo
          platform={sample.Platform}
          timeControl={item.GameTimeControl}
          date={sample.Date}
          link={sample.Link}
          onOpenGame={onOpenGame}
        />
      </div>
      <div className="sc-home-board" ref={boardRef}>
        <CorrectionBoard
          key={attempt}
          history={history}
          solveIndex={history.length - 1}
          solution={item.Solution}
          solveEvalCp={item.Evaluation}
          sideToMove={item.SideToMove}
          gamePlayedSan={item.PlayedMove}
          gameMoveType={item.MoveType}
          playIntro={attempt === 0}
          pieceHintSquare={outcome == null && hint && piece != null ? piece.square : null}
          answerUci={outcome == null && revealed ? item.Solution : null}
          onResult={onResult}
          navRef={navRef}
          onNavChange={(cursor) => {
            cursorRef.current = cursor;
          }}
        />
      </div>

      {/* The panel beside the board: one column, level with the board. */}
      <div className="sc-home-side">
        <header className="sc-home-head">
          <div className="sc-home-turn">
            <span className={`sc-home-turn-pill sc-home-turn-pill--${item.SideToMove}`}>
              <span className={`sc-home-disc sc-home-disc--${item.SideToMove}`} aria-hidden />
              {sideWord} to move
            </span>
            {lastMove != null ? (
              <span className="sc-home-last">
                Last move <strong>{lastMove}</strong>
              </span>
            ) : null}
          </div>
          <h1 className="sc-home-title">
            {mine ? 'You' : (sample.Player ?? 'A strong player')} made{' '}
            {mistakeWord(item.MoveType).article}{' '}
            <span className={`sc-home-blunder is-${moveTypeClass(item.MoveType) || 'blunder'}`}>
              {mistakeWord(item.MoveType).word}
            </span>{' '}
            here, can you find a better move?
          </h1>
          <p className="sc-home-players">
            <Player
              name={sample.White}
              rating={sample.WhiteRating}
              color="white"
              blundered={blunderer === 'white'}
            />
            <span className="sc-home-vs">vs</span>
            <Player
              name={sample.Black}
              rating={sample.BlackRating}
              color="black"
              blundered={blunderer === 'black'}
            />
          </p>
          {list}
        </header>

        <div className="sc-home-status" aria-live="polite">
          {outcome == null ? (
            <>
              {revealed ? (
                <p className="sc-home-say">
                  Play <strong>{answerSan}</strong> on the board.
                </p>
              ) : hint && piece != null ? (
                <p className="sc-home-say">
                  Move the <strong>{piece.name}</strong>.
                </p>
              ) : null}
              <div className="sc-home-help">
                {!hint && !revealed && piece != null ? (
                  <Button variant="primary" className="sc-home-hint" onClick={() => setHint(true)}>
                    <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                    Hint
                  </Button>
                ) : null}
                {!revealed ? (
                  <Button variant="link" size="sm" onClick={showAnswer}>
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    Show answer
                  </Button>
                ) : null}
              </div>
            </>
          ) : outcome.correct ? (
            <div className={`sc-home-verdict is-good${revealed ? '' : ' sc-home-win'}`}>
              {!revealed ? (
                <p className="sc-home-cheer">{CHEERS[solvedCount % CHEERS.length]}</p>
              ) : null}
              <p className="sc-home-verdict-head">
                <MoveTypeIcon moveType={outcome.moveType ?? MoveType.Best} />
                {outcome.playedSan} is {verdictWords(outcome.moveType ?? MoveType.Best)}.
              </p>
              <p className="sc-home-say">
                {mine
                  ? `In the game you played ${item.PlayedMove}.${revealed ? '' : ' Now you know better.'}`
                  : `${sample.Player ?? 'They'}${sample.Rating != null ? ` (${sample.Rating})` : ''} played ${item.PlayedMove} here.${revealed ? '' : ' You found what they missed.'}`}
              </p>
              <div className="sc-home-help">
                <Button variant="primary" size="lg" autoFocus onClick={onNext}>
                  {mine ? 'Next mistake' : 'Next blunder'}{' '}
                  <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                </Button>
                <Button variant="outline-secondary" size="lg" onClick={retry}>
                  <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                  Retry
                </Button>
                {solvedCount > 0 ? (
                  <span className="sc-home-streak">
                    <FontAwesomeIcon icon={faFire} className="me-1" />
                    {solvedCount} fixed
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={`sc-home-verdict is-${verdictClass}`}>
              <p className="sc-home-verdict-head">
                <MoveTypeIcon moveType={outcome.moveType} />
                {outcome.playedSan} is {verdictWords(outcome.moveType)}
              </p>
              <div className="sc-home-help">
                <Button variant="primary" onClick={retry}>
                  <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                  Try again
                </Button>
                {!hint && piece != null ? (
                  <Button
                    variant="outline-secondary"
                    className="sc-home-hint"
                    onClick={() => {
                      setHint(true);
                      retry();
                    }}
                  >
                    <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                    Hint
                  </Button>
                ) : null}
                <Button variant="link" size="sm" onClick={showAnswer}>
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  Show answer
                </Button>
              </div>
            </div>
          )}

          {footer}
        </div>
      </div>
      {nav ? <div className="sc-home-nav">{nav}</div> : null}
    </div>
  );
}

export default function FixOneMove(props: {
  initial: HomeSample[];
  mine: boolean;
  authenticated: boolean;
}): React.ReactNode {
  const { mine, authenticated } = props;
  const [samples, setSamples] = useState(props.initial);
  const [index, setIndex] = useState(0);
  const [solvedCount, setSolvedCount] = useState(0);

  // Past the end of the list, fetch a fresh one rather than stop: the visitor asked for another.
  // For your own mistakes that is the queue again, minus what you just repaired.
  const next = useCallback(() => {
    if (index + 1 < samples.length) {
      setIndex(index + 1);
      return;
    }
    fetch(mine ? '/api/Corrections/mine' : '/api/Corrections/samples')
      .then((r) => (r.ok ? (r.json() as Promise<HomeSample[]>) : []))
      .then((list) => {
        if (list.length > 0) {
          setSamples(list);
          setIndex(0);
        }
      })
      .catch(() => undefined);
  }, [index, samples.length, mine]);

  const sample = samples[index];
  return (
    <Puzzle
      key={`${sample.Item.MoveId}-${index}`}
      sample={sample}
      mine={mine}
      authenticated={authenticated}
      solvedCount={solvedCount}
      onSolved={() => setSolvedCount((n) => n + 1)}
      onNext={next}
      footer={
        authenticated ? (
          <LibraryBox mine={mine} waiting={samples.length - index - 1} />
        ) : (
          <OwnGamesForm />
        )
      }
    />
  );
}
